import {
  AfterViewInit,
  Directive,
  DOCUMENT,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
} from '@angular/core';

@Directive({
  selector: '[stickyScroll]',
  standalone: false,
})
export class StickyScrollDirective implements AfterViewInit, OnDestroy {
  @Input() offset: number = 16;
  @Input() mediaQuery: string = '(min-width: 768px)';
  @Input() scrollContainerSelector?: string;

  @Output() stickyTopOffsetChange = new EventEmitter<number>();

  private scrollContainer: HTMLElement | null = null;
  private scrollListener: (() => void) | null = null;
  private resizeListener: (() => void) | null = null;
  private mediaQueryList: MediaQueryList | null = null;
  private mediaQueryListener: (() => void) | null = null;
  private rafId: number | null = null;
  private lastPosition: number | null = null;
  private initialTop: number | null = null;
  private lastScrollTop: number = 0;
  private currentOffset: number = 0;

  constructor(
    private elementRef: ElementRef,
    private ngZone: NgZone,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngAfterViewInit(): void {
    if (!this.elementRef?.nativeElement) {
      return;
    }

    const selector = this.scrollContainerSelector || '#content-layout-body';
    this.scrollContainer = this.document.querySelector(selector) as HTMLElement;

    if (!this.scrollContainer) {
      return;
    }

    this.mediaQueryList = window.matchMedia(this.mediaQuery);
    this.mediaQueryListener = () => {
      this.updateStickyState();
    };
    this.mediaQueryList.addEventListener('change', this.mediaQueryListener);

    this.scrollListener = () => {
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
      }
      this.rafId = this.ngZone.runOutsideAngular(() => {
        return requestAnimationFrame(() => {
          this.handleScroll();
        });
      });
    };

    this.resizeListener = () => {
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
      }
      this.rafId = requestAnimationFrame(() => {
        this.calculateInitialPosition();
        this.updateStickyState();
      });
    };

    this.scrollContainer.addEventListener('scroll', this.scrollListener, {
      passive: true,
    });
    window.addEventListener('resize', this.resizeListener, { passive: true });

    setTimeout(() => {
      this.calculateInitialPosition();
      this.updateStickyState();
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.scrollContainer && this.scrollListener) {
      this.scrollContainer.removeEventListener('scroll', this.scrollListener);
    }

    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }

    if (this.mediaQueryList && this.mediaQueryListener) {
      this.mediaQueryList.removeEventListener(
        'change',
        this.mediaQueryListener,
      );
    }

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.scrollContainer = null;
    this.scrollListener = null;
    this.resizeListener = null;
    this.mediaQueryList = null;
    this.mediaQueryListener = null;
  }

  private calculateInitialPosition(): void {
    if (!this.elementRef?.nativeElement || !this.scrollContainer) {
      return;
    }
    const element = this.elementRef.nativeElement;
    this.initialTop = this.lastPosition = element.offsetTop;
  }

  private resetScrollState(): boolean {
    if (!this.mediaQueryList?.matches) {
      if (this.currentOffset !== 0) {
        this.currentOffset = 0;
        this.lastPosition = this.initialTop;
        this.ngZone.run(() => {
          this.stickyTopOffsetChange.emit(0);
        });
      } else {
        this.lastPosition = this.initialTop;
      }
      return true;
    }
    return false;
  }

  private handleScroll(): void {
    if (
      !this.scrollContainer ||
      !this.elementRef?.nativeElement ||
      this.resetScrollState()
    ) {
      return;
    }
    this.calculateStickyOffset();
  }

  private updateStickyState(): void {
    if (this.resetScrollState()) {
      return;
    }
    if (this.initialTop === null) {
      this.calculateInitialPosition();
    }
    this.calculateStickyOffset();
  }

  private calculateStickyOffset(): void {
    if (
      !this.scrollContainer ||
      !this.elementRef?.nativeElement ||
      this.initialTop === null
    ) {
      return;
    }

    const element = this.elementRef.nativeElement;
    const containerRect = this.scrollContainer.getBoundingClientRect();
    const containerHeight = containerRect.height;
    const elementHeight = element.offsetHeight;
    const scrollTop = this.scrollContainer.scrollTop;

    if (elementHeight <= containerHeight - this.offset * 2) {
      if (this.currentOffset !== this.offset) {
        this.currentOffset = this.offset;
        this.ngZone.run(() => {
          this.stickyTopOffsetChange.emit(this.offset);
        });
      }
      this.lastScrollTop = scrollTop;
      return;
    }

    // This is where the top of the element would be relative
    // to the top of the container, if it were not sticky
    const elementTop = this.initialTop - scrollTop;

    const previousPosition = this.lastPosition;
    this.lastPosition = elementTop;
    if (previousPosition === null || elementTop === previousPosition) {
      // No change in position
      return;
    }

    const elementOverflow = containerHeight - elementHeight;
    const scrollDelta = scrollTop - this.lastScrollTop;
    this.lastScrollTop = scrollTop;

    let newOffset: number;

    if (elementTop < previousPosition) {
      // Scrolling down
      const bottomOffset = elementOverflow - this.currentOffset;
      const maxOffset = elementOverflow - this.offset;
      if (bottomOffset < this.offset) {
        newOffset = Math.max(maxOffset, this.currentOffset - scrollDelta);
      } else {
        newOffset = maxOffset;
      }
    } else {
      // Scrolling up
      if (this.currentOffset < this.offset) {
        newOffset = Math.min(this.offset, this.currentOffset - scrollDelta);
      } else {
        newOffset = this.offset;
      }
    }

    if (this.currentOffset !== newOffset) {
      this.currentOffset = newOffset;
      this.ngZone.run(() => {
        this.stickyTopOffsetChange.emit(newOffset);
      });
    }
  }
}
